// feature/role-views — Admin · cases marked as creado por error.
// feature/inline-edit-soft-delete.4 — hard delete requires typing "ELIMINAR" to confirm.
import React, { useEffect, useState } from "react";
import { admin, cases as casesApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill } from "../../components/Pills";
import { useToast } from "../../components/Toast";
import { useCaseLauncher } from "../../components/CaseLauncher";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function ErrorQueue() {
  const toast = useToast();
  const launcher = useCaseLauncher();
  const [data, setData] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);

  function load() {
    setData(null);
    admin.errorCases().then(r => setData(r.data)).catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);

  async function doRestore() {
    try {
      await admin.restoreErrorCase(restoreTarget.id);
      toast.push("Caso restaurado");
      setRestoreTarget(null); load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }
  async function doDelete(motivo) {
    try {
      await casesApi.hardDelete(deleteTarget.id, motivo);
      toast.push("Caso eliminado permanentemente");
      setDeleteTarget(null); load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <h1 style={{ margin: 0 }}>Casos marcados por error</h1>
      <div className="muted" style={{ marginTop: -8 }}>
        Reparto puede marcar un caso como creado por error. Aquí puede administrarlos: restaurarlos al flujo normal o eliminarlos permanentemente.
      </div>

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="No hay casos marcados como error" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Caso</th><th>Tipo de actuación</th><th>Accionante</th>
                <th>Marcado por</th><th>Fecha</th><th>Motivo</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.id}>
                  <td onClick={() => launcher.open(c.id)} style={{ cursor: "pointer" }}><strong>{c.case_number}</strong></td>
                  <td><TipoPill tipo={c.tipo} /></td>
                  <td>{c.accionante_nombre || "—"}</td>
                  <td>{c.marcado_por_name || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmt(c.marcado_error_at)}</td>
                  <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.error_motivo || ""}>
                    {c.error_motivo || "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="secondary sm" onClick={() => setRestoreTarget(c)}>Restaurar</button>
                    <button className="ghost sm" style={{ color: "var(--color-danger)" }} onClick={() => setDeleteTarget(c)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Confirm
        open={!!restoreTarget}
        title="Restaurar caso"
        message={restoreTarget ? `${restoreTarget.case_number} volverá al estado "nuevo" para que Reparto lo enrute.` : ""}
        onClose={() => setRestoreTarget(null)}
        onConfirm={doRestore}
        confirmText="Restaurar"
      />
      <HardDeleteModal
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={doDelete}
      />
    </div>
  );
}

// feature/inline-edit-soft-delete.4 — hard-delete modal. Requires the admin to type "ELIMINAR" verbatim
// before the confirm button activates. Optional motivo is captured in the
// audit_log payload (written by the backend BEFORE the cascade DELETE).
function HardDeleteModal({ target, onClose, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (target) { setTyped(""); setMotivo(""); } }, [target]);

  if (!target) return null;
  const armed = typed === "ELIMINAR";
  async function go() {
    setBusy(true);
    try { await onConfirm(motivo); } finally { setBusy(false); }
  }
  return (
    <Modal
      open={!!target}
      title="Eliminar permanentemente"
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button className="danger" disabled={!armed || busy} onClick={go}>
          {busy && <Spinner color="currentColor" />} Eliminar definitivamente
        </button>
      </>}
    >
      <p>
        Esta acción no se puede deshacer. Se eliminarán permanentemente: el caso{" "}
        <strong>{target.case_number}</strong>, todos sus documentos, todos sus insumos,
        y todas las entradas del registro de auditoría relacionadas. Para confirmar, escriba{" "}
        <strong>ELIMINAR</strong> abajo.
      </p>
      <div style={{ marginTop: 12 }}>
        <label>Confirmación</label>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="ELIMINAR"
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Motivo (opcional, queda en el registro de auditoría)</label>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </div>
    </Modal>
  );
}
